import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, MessageSquare, Trash2, AlertCircle, ChevronRight, FileText, Loader2, RefreshCw, AlertTriangle, Shield, Zap, Lock } from 'lucide-react';
import { deleteScan } from '../../hooks/useScans';
import { formatMessageCountShort } from '../../utils/scanReport';
// Pattern type that can be either a string or an object with type/severity
type PatternItem = string | {
  type: string;
  severity?: string;
};
interface ScanCardProps {
  scanId: string;
  title: string;
  timeAgo: string;
  score: number;
  riskLevel: string;
  /** Real message count, or null when unknown. */
  messages: number | null;
  tags?: string[];
  status?: 'uploading' | 'processing' | 'completed' | 'failed';
  patterns?: PatternItem[];
  summary?: string;
  isUnlocked?: boolean;
}
export function ScanCard({
  scanId,
  title,
  timeAgo,
  score,
  riskLevel,
  messages,
  tags = [],
  status = 'completed',
  patterns = [],
  summary,
  isUnlocked = true
}: ScanCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();
  const isProcessing = status === 'uploading' || status === 'processing';
  // DEBUG: Log scan data to verify backend values
  console.log('[ScanCard] Render:', {
    scanId,
    score,
    riskLevel,
    status
  });
  // FIX: Use riskLevel prop directly instead of recalculating from score
  const getRiskBadgeStyle = () => {
    if (status === 'failed') return 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30';
    if (isProcessing) return 'bg-gray-800 text-gray-400 border-gray-700';
    // Use riskLevel from backend - DO NOT recalculate from score
    const normalizedRisk = riskLevel?.toLowerCase();
    if (normalizedRisk === 'high' || normalizedRisk === 'high risk') return 'bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30';
    if (normalizedRisk === 'medium' || normalizedRisk === 'medium risk') return 'bg-orange-500/10 text-orange-500 border-orange-500/30';
    return 'bg-green-500/10 text-green-500 border-green-500/30';
  };
  // FIX: Use riskLevel prop directly instead of recalculating from score
  const getScoreColor = () => {
    const normalizedRisk = riskLevel?.toLowerCase();
    if (normalizedRisk === 'high' || normalizedRisk === 'high risk') return 'text-[#FF0000]';
    if (normalizedRisk === 'medium' || normalizedRisk === 'medium risk') return 'text-orange-500';
    return 'text-green-500';
  };
  // Helper to extract pattern info
  const getPatternInfo = (pattern: PatternItem) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM'
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM'
    };
  };
  // Helper for severity badge styling
  const getSeverityStyle = (severity: string) => {
    const s = severity?.toUpperCase();
    if (s === 'HIGH') return 'bg-[#FF0000]/20 text-[#FF0000] border-[#FF0000]/40';
    if (s === 'MEDIUM') return 'bg-orange-500/20 text-orange-400 border-orange-500/40';
    return 'bg-green-500/20 text-green-400 border-green-500/40';
  };
  // Helper for pattern icon
  const getPatternIcon = (type: string) => {
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertTriangle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('aggress')) return Zap;
    return AlertCircle;
  };
  // Get display patterns (up to 3)
  const displayPatterns = patterns.slice(0, 3);
  const hasMorePatterns = patterns.length > 3;
  const getStatusText = () => {
    if (status === 'uploading') return 'Uploading...';
    if (status === 'processing') return 'Processing...';
    if (status === 'failed') return 'Analysis Failed';
    return riskLevel;
  };
  const getStatusIcon = () => {
    if (status === 'failed') return <AlertCircle size={24} className="text-[#FF0000]" data-id="element-1074" data-mp="2b5p4cfly1ude" />;
    if (isProcessing) return <Loader2 size={24} className="text-[#FF0000] animate-spin" data-id="element-1075" data-mp="2qiq2l9gq4k6b" />;
    if (status === 'completed') return <FileText size={24} data-id="element-1076" data-mp="2lpdt66km8jy5" />;
    return <AlertCircle size={24} data-id="element-1077" data-mp="2grlriku65jgr" />;
  };
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this scan?')) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteScan(scanId);
    } catch (error) {
      console.error('Failed to delete scan:', error);
      alert('Failed to delete scan. Please try again.');
      setIsDeleting(false);
    }
  };
  // Scans are read-only from the client, so a failed scan is re-run as a new scan.
  const handleRetry = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRetrying(true);
    navigate('/upload');
  };
  // Skeleton Loader for Processing State
  if (isProcessing) {
    return <div className="relative bg-[#0a0a0a] border border-gray-800 p-5 flex items-center gap-5" data-id="element-1078" data-mp="1k01nmyjvqxz9">
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-700" data-id="element-1079" data-mp="3vqj8nil0a1yi" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-700" data-id="element-1080" data-mp="4iww10gb6p5i" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-700" data-id="element-1081" data-mp="8j75wo781zpn" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-700" data-id="element-1082" data-mp="dbifyjvof1vx" />

        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gray-800 border border-gray-700" data-id="element-1083" data-mp="i90dzf85nwp5">
          <Loader2 size={20} className="text-gray-600 animate-spin" data-id="element-1084" data-mp="n71w9xxrbrzk" />
        </div>
        <div className="flex-1 min-w-0 space-y-2" data-id="element-1085" data-mp="s9ar6zu6x44b">
          <div className="h-4 w-1/3 bg-gray-800 animate-pulse" data-id="element-1086" data-mp="w9kuu5uifig0" />
          <div className="h-3 w-1/4 bg-gray-900 animate-pulse" data-id="element-1087" data-mp="w8ryzzx3adcn" />
        </div>
        <div className="text-sm font-mono text-gray-600 uppercase tracking-wide" data-id="element-1088" data-mp="2u0nvbmoc37zb">
          Processing...
        </div>
      </div>;
  }
  // Failed State
  if (status === 'failed') {
    return <div className={`relative bg-[#0a0a0a] border-2 border-[#FF0000]/30 p-5 flex items-center gap-5 ${isDeleting || isRetrying ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-1089" data-mp="578y6hnfe6vm">
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF0000]" data-id="element-1090" data-mp="e871exq1iad5" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF0000]" data-id="element-1091" data-mp="a4hh4kgeeexx" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF0000]" data-id="element-1092" data-mp="o4jym5ksn6cn" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF0000]" data-id="element-1093" data-mp="j70eip1ezzcs" />

        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000]" data-id="element-1094" data-mp="xu3ed2d7mk3a">
          <AlertCircle size={24} data-id="element-1095" data-mp="sy4wzyqau2cd" />
        </div>

        <div className="flex-1 min-w-0" data-id="element-1096" data-mp="16v1lhtiy37q3">
          <div className="flex items-center gap-3 mb-2" data-id="element-1097" data-mp="16w2vduo85o68">
            <h4 className="text-base font-bold text-white truncate uppercase tracking-tight" data-id="element-1098" data-mp="11xhvxxgsr8iu">
              {title}
            </h4>
            <span className="px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest bg-[#FF0000]/10 text-[#FF0000] border-[#FF0000]/30" data-id="element-1099" data-mp="1lj1lnmbcicjd">
              Analysis Failed
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500 font-mono" data-id="element-1100" data-mp="1cjwp2ljcgt9u">
            <span className="flex items-center gap-1.5" data-id="element-1101" data-mp="1ghugzw4i1tgx">
              <Clock size={14} className="text-gray-600" data-id="element-1102" data-mp="12rijtit254ix" />
              {timeAgo}
            </span>
            <span className="text-[#FF0000]" data-id="element-1103" data-mp="17j8vpt7n80cg">
              Forensic analysis could not be completed
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-1104" data-mp="svnts2mi8764">
          <button onClick={handleRetry} disabled={isRetrying} className="flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white 
                     hover:bg-[#FF0000]/90 transition-all duration-200 font-bold text-sm uppercase tracking-wider
                     disabled:opacity-50 disabled:cursor-not-allowed" title="Retry analysis" data-id="element-1105" data-mp="xs5yx3lqy7iz">
            <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} data-id="element-1106" data-mp="zawfu16v05fv" />
            {isRetrying ? 'Retrying...' : 'Retry'}
          </button>

          <button onClick={handleDelete} disabled={isDeleting} className="p-2 text-gray-600 hover:text-[#FF0000] hover:bg-[#FF0000]/10 
                     transition-all duration-200" title="Delete scan" aria-label="Delete scan" data-id="element-1107" data-mp="ue623kij1lxm">
            <Trash2 size={18} data-id="element-1108" data-mp="gill4umczdsu" />
          </button>
        </div>
      </div>;
  }
  // Completed State - UPDATED
  return <Link to={`/analysis?id=${scanId}`} className="block group" data-id="element-1109" data-mp="6ms7t90tfga0">
      <div className={`relative bg-[#0a0a0a] border border-gray-800 p-5 hover:border-[#FF0000]/50 transition-all duration-200 ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-1110" data-mp="6kfha7gzrxlc">
        {/* Corner Brackets */}
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-1111" data-mp="299fppj3x5i0n" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-1112" data-mp="2e6xq2l3veox1" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-1113" data-mp="2i814r4a6mewo" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-700 group-hover:border-[#FF0000] transition-colors" data-id="element-1114" data-mp="1qjh7ia6eq8ja" />

        <div className="flex items-start gap-5" data-id="element-1115" data-mp="1ukj8avfz2snd">
          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center transition-colors bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000] group-hover:bg-[#FF0000]/20" data-id="element-1116" data-mp="1zikqle5kqnxs">
            <FileText size={24} data-id="element-1117" data-mp="16vgq9mhfexsc" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0" data-id="element-1118" data-mp="1bticuhe29f8k">
            {/* Title Row */}
            <div className="flex items-center gap-3 mb-2" data-id="element-1119" data-mp="1brfcb3fa83pl">
              <h4 className="text-base font-bold text-white truncate group-hover:text-[#FF0000] transition-colors uppercase tracking-tight" data-id="element-1120" data-mp="3dkv8gkcyx7ur">
                {title}
              </h4>
              <span className={`px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest flex-shrink-0 ${getRiskBadgeStyle()}`} data-id="element-1121" data-mp="3mm1tn6qkb6uc">
                {getStatusText()}
              </span>
              {!isUnlocked && status === 'completed' && <span className="px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest flex-shrink-0 bg-gray-800 text-gray-400 border-gray-700" data-id="element-1122" data-mp="2yw7geo2bzg9k">
                  <Lock size={10} className="inline mr-1" data-id="element-1123" data-mp="33tqu9mv49r0z" />
                  Locked
                </span>}
            </div>

            {/* Meta Row */}
            <div className="flex items-center gap-4 text-xs text-gray-500 font-mono mb-3" data-id="element-1124" data-mp="2b6d4q9tqzxqw">
              <span className="flex items-center gap-1.5" data-id="element-1125" data-mp="2g57opf3gbscl">
                <Clock size={14} className="text-gray-600" data-id="element-1126" data-mp="28mq6mkhif580" />
                {timeAgo}
              </span>
              {!isProcessing && <span className="flex items-center gap-1.5" data-id="element-1127" data-mp="23oyi80g2q18k">
                  <MessageSquare size={14} className="text-gray-600" data-id="element-1128" data-mp="1yoaimqds4c2q" />
                  {formatMessageCountShort(messages)}
                </span>}
              {status === 'completed' && <span className="hidden sm:inline" data-id="element-1129" data-mp="1podzt5e2m7fl">
                  {patterns.length} {patterns.length === 1 ? 'pattern' : 'patterns'} detected
                </span>}
            </div>

            {/* Patterns Section */}
            {displayPatterns.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-1130" data-mp="1ayzlrjzjv1tv">
                {displayPatterns.map((pattern, idx) => {
              const info = getPatternInfo(pattern);
              const PatternIcon = getPatternIcon(info.type);
              return <div key={idx} className={`inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] font-mono uppercase tracking-wide ${getSeverityStyle(info.severity)}`} data-id="element-1131" data-mp="120e73zx786yd" data-mpi={idx}>
                      <PatternIcon size={12} data-id="element-1132" data-mp="id9qcvfjo24q" />
                      <span className="truncate max-w-[100px]" data-id="element-1133" data-mp="iektqu52v7vb">
                        {info.type}
                      </span>
                    </div>;
            })}
                {hasMorePatterns && <span className="text-[10px] font-mono text-gray-500" data-id="element-1134" data-mp="1f2ioly16bb2m">
                    +{patterns.length - 3} more
                  </span>}
              </div>}

            {/* Summary Message - Show AI-generated summary if available */}
            {summary && <p className="text-xs text-gray-400 line-clamp-2 font-mono leading-relaxed mb-3" data-id="element-1135" data-mp="1u0b08f3lnfbj">
                {summary}
              </p>}

            {/* Locked State CTA */}
            {!isUnlocked && status === 'completed' && <div className="mt-3" data-id="element-1136" data-mp="28407w7sk6k89">
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30 hover:bg-[#FF0000] hover:text-white transition-all font-bold text-xs uppercase tracking-wider" data-id="element-1137" data-mp="243y700unduu1">
                  <Lock size={12} data-id="element-1138" data-mp="2cw07u08mpd4u" />
                  View Full Report
                </button>
              </div>}
          </div>

          {/* Right Side - Score & Actions */}
          <div className="flex items-center gap-6 flex-shrink-0" data-id="element-1139" data-mp="1l5vom4wluw57">
            <div className="text-right hidden sm:block" data-id="element-1140" data-mp="1l7ggfmrjc0zd">
              <div className={`text-2xl font-black leading-none font-mono ${getScoreColor()}`} data-id="element-1141" data-mp="1z5ob1o13mq7d">
                {score}
              </div>
              <div className="text-[10px] font-mono text-gray-600 uppercase tracking-widest mt-1" data-id="element-1142" data-mp="3ajil9od8j3f6">
                Threat Level
              </div>
            </div>

            <div className="flex items-center gap-1" data-id="element-1143" data-mp="35nbe5s93rlvn">
              <button onClick={handleDelete} disabled={isDeleting} className="p-2 text-gray-600 hover:text-[#FF0000] hover:bg-[#FF0000]/10 
                         transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100" title="Delete scan" aria-label="Delete scan" data-id="element-1144" data-mp="1f5551v2rah3b">
                <Trash2 size={18} data-id="element-1145" data-mp="1y1o7y2myv8rf" />
              </button>

              <div className="p-2 text-gray-700 group-hover:text-[#FF0000] transition-colors" data-id="element-1146" data-mp="231splvzm29j8">
                <ChevronRight size={20} data-id="element-1147" data-mp="22zg1f139f2xq" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>;
}